import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Briefcase, Box } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const InteractiveNavigationBubbles = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [hoveredBubble, setHoveredBubble] = useState<string | null>(null);

  const bubbles = [
    {
      id: 'products',
      label: t('bubbles.products'),
      subtitle: 'Uși și mobilier',
      path: '/products/doors',
      icon: Package,
    },
    {
      id: 'projects',
      label: t('bubbles.projects'),
      subtitle: 'Realizările noastre',
      path: '/projects',
      icon: Briefcase,
    },
    {
      id: 'semifabricate',
      label: t('bubbles.semifabricate'),
      subtitle: 'Produse semifinite',
      path: '/semifabricate',
      icon: Box,
    },
  ];

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-center items-center gap-16 md:gap-24">
          {bubbles.map((bubble) => {
            const Icon = bubble.icon;
            const isHovered = hoveredBubble === bubble.id;
            
            return (
              <button
                key={bubble.id}
                onClick={() => navigate(bubble.path)}
                onMouseEnter={() => setHoveredBubble(bubble.id)}
                onMouseLeave={() => setHoveredBubble(null)}
                className={`group relative w-64 h-64 md:w-80 md:h-80 overflow-hidden rounded-full cursor-pointer
                  transition-all duration-500 ease-out animate-gentle-breathe
                  focus:outline-none focus:ring-2 focus:ring-primary/30
                  ${isHovered ? '-translate-y-2 scale-105' : 'translate-y-0 scale-100'}`}
                aria-label={bubble.label}
              >
                {/* Light reflection at top */}
                <div 
                  className="absolute -top-1/3 left-0 right-0 h-1/2 bg-gradient-to-b from-white/30 via-white/10 to-transparent 
                    rounded-full blur-xl pointer-events-none z-20"
                />
                
                {/* Glass border edge highlight */}
                <div className="absolute inset-0 rounded-full border-2 border-white/50 dark:border-white/30 pointer-events-none z-10" />
                
                {/* Inner glass shadow */}
                <div className="absolute inset-2 rounded-full shadow-inner shadow-black/10 dark:shadow-black/30 pointer-events-none z-10" />
                
                {/* Glassmorphic card with multiple layers */}
                <div
                  className={`relative h-full backdrop-blur-2xl bg-white/25 dark:bg-white/10
                    border-2 border-white/40 dark:border-white/20 
                    transition-all duration-500 rounded-full
                    ${isHovered ? 'shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)]' : 'shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)]'}`}
                >
                  {/* Frosted glass texture overlay */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 via-transparent to-white/5 pointer-events-none" />
                  
                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center justify-center h-full px-8 gap-4">
                    {/* Icon */}
                    <div
                      className={`transition-all duration-500 ease-out
                        ${isHovered ? '-translate-y-1 scale-110' : 'translate-y-0 scale-100'}`}
                    >
                      <Icon
                        className="w-16 h-16 md:w-20 md:h-20 text-primary/80 group-hover:text-primary 
                          transition-colors duration-500 drop-shadow-lg"
                        strokeWidth={1.5}
                      />
                    </div>

                    {/* Label */}
                    <div className="text-center space-y-1">
                      <h3 className="text-2xl md:text-3xl font-bodoni font-semibold text-foreground drop-shadow">
                        {bubble.label}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground font-light">
                        {bubble.subtitle}
                      </p>
                    </div>

                    {/* Hover indicator */}
                    <div
                      className={`flex items-center gap-2 text-xs md:text-sm text-primary transition-all duration-500
                        ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                    >
                      <span className="font-medium">{t('bubbles.explore')}</span>
                      <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
                    </div>
                  </div>

                  {/* Subtle gradient overlay on hover */}
                  <div 
                    className={`absolute inset-0 rounded-full bg-gradient-to-br from-primary/10 via-transparent to-primary/5
                      transition-opacity duration-500 pointer-events-none
                      ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default InteractiveNavigationBubbles;
